import React, { useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { DropDownListComponent } from "@syncfusion/ej2-react-dropdowns";
import classNames from "classnames";

/**
 * PropertyTypeSelect — Syncfusion DropDownList pre-wired to the propertyTypes Redux store.
 *
 * Decorated replacement for the legacy react-select `PropertyTypeSelectBox`.
 * Emits the selected `parameterId` via `onChange` and notifies when `allowUnits`
 * changes via the `onAllowUnitsChange` callback.
 */
export default function PropertyTypeSelect({
  placeholder = "Property Type *",
  value,
  onChange,
  onAllowUnitsChange,
  error,
  disabled = false,
  className,
  floatLabelType = "Auto",
  ...props
}) {
  const { propertyTypes: records } = useSelector(state => state.propertyTypes);

  const dataSource = useMemo(() => {
    if (!records?.length) return [];
    return records.map(pt => ({
      text: pt.name,
      value: pt.parameterId,
      allowUnits: pt.configuration?.allowUnits ?? false,
    }));
  }, [records]);

  const handleChange = useCallback(
    e => {
      const item = e.itemData;
      onChange?.(item?.value ?? undefined);
      onAllowUnitsChange?.(item?.allowUnits ?? false);
    },
    [onChange, onAllowUnitsChange],
  );

  return (
    <div className={classNames("select-input-wrapper", className)}>
      <DropDownListComponent
        placeholder={placeholder}
        floatLabelType={floatLabelType}
        dataSource={dataSource}
        fields={{ text: "text", value: "value" }}
        value={value}
        change={handleChange}
        enabled={!disabled}
        cssClass={classNames("e-outline", { "e-error": !!error })}
        {...props}
      />
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}

PropertyTypeSelect.propTypes = {
  placeholder: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func,
  /** Called with boolean whenever the selected type toggles allowUnits */
  onAllowUnitsChange: PropTypes.func,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  floatLabelType: PropTypes.oneOf(["Auto", "Always", "Never"]),
};

